import PixelWorker from './pixel-worker?sharedworker';
import DedupedPixelWorker from './deduped-pixel-worker?sharedworker';
import type {
  InboundMessageData,
  WorkerMessageData,
  WorkerSingleMessageData,
} from './pixelDataSharedWorkerTypes';
import { PixelDataSharedWorkerError } from './pixelDataSharedWorkerTypes';
import { logger } from 'modules/logger';
import analytics from 'modules/analytics';
import { globalContext, CUSTOM_TIMINGS } from 'modules/analytics/constants';
import type { WorkerLoadedImagingProvider } from 'domains/viewer/ViewportDre/modules/imaging/WorkerLoadedImagingProvider';
import type { FullSingleLayerStack } from 'domains/viewer/ViewportsConfigurations/types';
import { AnalyticEventName } from 'modules/analytics/analytics';

export interface PixelDataPort extends MessagePort {
  // Function overload with a narrower type than `any`, compared to `MessagePort`
  postMessage(message: InboundMessageData, transfer?: Transferable[]): void;
  postMessage(message: any, transfer: Transferable[]): void;
  postMessage(message: any, options: StructuredSerializeOptions): void;
}

export interface PixelDataSharedWorker extends SharedWorker {
  port: PixelDataPort;
}

export const providerMap: Map<string, WorkerLoadedImagingProvider<FullSingleLayerStack>> = new Map<
  string,
  WorkerLoadedImagingProvider<FullSingleLayerStack>
>();

export const defaultOnMessage = (messageEvent: MessageEvent) => {
  if (messageEvent.data == null) {
    providerMap.forEach((provider) => {
      provider.failLoading(PixelDataSharedWorkerError.OutOfMemory);
      provider._resolveLoad();
    });
    return;
  }

  const data = messageEvent.data as WorkerMessageData;

  if (data.type === 'batched-messages') {
    data.messages.forEach(handleWorkerMessage);
  } else {
    handleWorkerMessage(data);
  }
};

export function createPixelDataSharedWorker(
  useDedupedPixelWorker: boolean = false,
  onmessage?: (messageEvent: MessageEvent) => void,
  // VX-4156 (VIEWER_SW_PORT_PRUNING): when true, post the `enable-pruning` opt-in so the
  // SharedWorker's prune() can reclaim this client's port once it goes silent. Defaults false so
  // only callers that run the ping watchdog opt in — the viewer threads the flag via
  // useOpenPixelDataSharedWorker; non-viewer/transient callers (logout/close, monitoring) stay out
  // and are never reclaimed, preserving the kill-switch contract (flag off -> nothing reclaimed).
  enablePortPruning: boolean = false
): PixelDataSharedWorker {
  const pixelDataSharedWorker: PixelDataSharedWorker = useDedupedPixelWorker
    ? new DedupedPixelWorker({ name: 'Pixel Data Shared Worker [deduped]' })
    : new PixelWorker({ name: 'Pixel Data Shared Worker [legacy]' });
  pixelDataSharedWorker.port.start();

  pixelDataSharedWorker.port.onmessage = onmessage ?? defaultOnMessage;

  // VX-4156: opt this client's port into liveness-based pruning. The SharedWorker's prune() reclaims
  // a port only after it has gone silent (see LRUPortCache heartbeat liveness); `enable-pruning` marks
  // this port eligible so an orphan left by a torn-down tab is reclaimed, while ports that never opt
  // in (pruning flag off) are left alone. Posted once at construction; the port is then kept fresh by
  // the page's ping watchdog and ordinary load traffic, so a live page is never reclaimed.
  if (enablePortPruning) {
    pixelDataSharedWorker.port.postMessage({ type: 'enable-pruning' });
  }

  pixelDataSharedWorker.port.postMessage({ type: 'ping' });

  return pixelDataSharedWorker;
}

// #region Analytics & Tracking
export const analyticsStackInitialLoadsTracker: Set<string> = new Set<string>();
export const analyticsStackUnhangedLoadsTracker: Set<string> = new Set<string>();
export const providerTracker: Map<
  string,
  WorkerLoadedImagingProvider<FullSingleLayerStack>
> = new Map<string, WorkerLoadedImagingProvider<FullSingleLayerStack>>();

let hangedFramesCount = 0;
let hangedLoadsDoneTime = 0;
export let hasHit25PercentThreshold = false;

export function resetImagingProviderTrackers() {
  hangedFramesCount = 0;
  hangedLoadsDoneTime = 0;
  analyticsStackInitialLoadsTracker.clear();
  analyticsStackUnhangedLoadsTracker.clear();
  providerTracker.clear();
  hasHit25PercentThreshold = false;
}

export function checkInitialLoads25PercentComplete(): boolean {
  // cache result once we reach the threshold;
  if (hasHit25PercentThreshold) return true;
  hasHit25PercentThreshold =
    providerMap.size > 0 &&
    analyticsStackInitialLoadsTracker.size > 0 &&
    Array.from(providerMap.entries())
      .filter(([stackSmid, provider]) => analyticsStackInitialLoadsTracker.has(stackSmid))
      .every(([stackSmid, provider]) => {
        const stackSize = provider.stackSize;

        switch (provider.loadType) {
          case 'none':
            return true;
          case 'initial':
            return provider._frameLoadingStateMap[stackSmid].size >= 1;
          case 'full':
            return provider._frameLoadingStateMap[stackSmid].size >= stackSize / 4;
          default:
            return false;
        }
      });

  return hasHit25PercentThreshold;
}
// #endregion

/** App-side handlers for messages received FROM the worker */
export function handleWorkerMessage(data: WorkerSingleMessageData) {
  const provider = providerMap.get('stackSmid' in data ? data.stackSmid : '');
  switch (data.type) {
    case 'load-complete':
      analytics.timing(CUSTOM_TIMINGS.viewer.workerImagingProvider.loadComplete);
      if (analyticsStackInitialLoadsTracker.has(data.stackSmid)) {
        analyticsStackInitialLoadsTracker.delete(data.stackSmid);
        analytics.loadedFrames += providerTracker.get(data.stackSmid)?.stackSize ?? 0;
        providerTracker.delete(data.stackSmid);
        if (analyticsStackInitialLoadsTracker.size === 0) {
          analytics.timing(CUSTOM_TIMINGS.viewer.workerImagingProvider.allLoadsComplete);
          hangedLoadsDoneTime = performance.now();
          const time = hangedLoadsDoneTime - analytics.sessionStartTime;
          hangedFramesCount = analytics.loadedFrames;
          const averageHangedFPS = analytics.loadedFrames / (time / 1000);
          analytics.addViewContext(
            globalContext.viewer.loadMetrics.hangedFramesPerSecond,
            averageHangedFPS
          );
        }
      }

      if (analyticsStackUnhangedLoadsTracker.has(data.stackSmid)) {
        analyticsStackUnhangedLoadsTracker.delete(data.stackSmid);
        analytics.loadedFrames += providerTracker.get(data.stackSmid)?.stackSize ?? 0;
        providerTracker.delete(data.stackSmid);
        if (
          analyticsStackInitialLoadsTracker.size === 0 &&
          analyticsStackUnhangedLoadsTracker.size === 0
        ) {
          analytics.timing(CUSTOM_TIMINGS.viewer.workerImagingProvider.unhangedLoadsComplete);
          const unhangedLoadsDoneTime = performance.now();
          const time = unhangedLoadsDoneTime - analytics.sessionStartTime;

          const averageUnhangedFPS =
            (analytics.loadedFrames - hangedFramesCount) /
            ((unhangedLoadsDoneTime - hangedLoadsDoneTime) / 1000);
          analytics.addViewContext(
            globalContext.viewer.loadMetrics.unhangedFramesPerSecond,
            averageUnhangedFPS
          );
          const averageAllFPS = analytics.loadedFrames / (time / 1000);
          analytics.addViewContext(
            globalContext.viewer.loadMetrics.allFramesPerSecond,
            averageAllFPS
          );
        }
      }

      provider?._resolveLoad();
      break;
    case 'handle-frame':

    case 'handle-initial-frame':
      if (data.type === 'handle-initial-frame') {
        analytics.timing(CUSTOM_TIMINGS.viewer.workerImagingProvider.handleInitialFrame);
      }
      provider?.handleFrame(data);

      if (checkInitialLoads25PercentComplete()) {
        analytics.timing(CUSTOM_TIMINGS.viewer.workerImagingProvider.loads25PercentComplete);
      }

      break;

    case 'load-error':
      const { error } = data;
      if (provider != null) {
        provider.failLoading(error);
        provider._resolveLoad();
        logger.error(`load-error for ${provider.stack.smid}`, error);
      } else {
        logger.error(`load-error for unknown stack`, error);
      }
      break;
    case 'analytics-error':
      analytics.error(data.error, data.data);
      break;
    case 'analytics-report':
      // Create a new RUM action to tag the session with the per-stack load metrics
      analytics.track(`viewer.sys.stack_load_complete` as unknown as AnalyticEventName, {
        loadMetrics: data.data,
      });
      break;
    case 'analytics-track':
      analytics.track(data.name, data.data);
      break;
    case 'transfer-cancelled':
      logger.info('Transfer was cancelled');
      break;
    case 'pong':
      break;
    default:
      // @ts-expect-error Runtime check, though we'll never reach this condition if we're fully
      // typesafe
      throw new Error(`Unknown message received: ${data.type}`);
  }
}

export const clearProviderMap = (): void => {
  for (const [, provider] of providerMap) {
    provider.dispose();
  }
  providerMap.clear();
  resetImagingProviderTrackers();
};

/**
 * Sends a terminate message to both shared worker variants and waits up to 500 ms for each to
 * acknowledge before closing. The acknowledgement is the broadcast-back of `{ type: 'terminate' }`
 * that PixelWorkerConnection emits to all ports before calling self.close(). Returns one boolean
 * per worker: true = termination confirmed, false = timed out or connection failed.
 */
export async function terminatePixelDataSharedWorkers(): Promise<boolean[]> {
  const terminateWorker = (factory: () => SharedWorker, workerName: string): Promise<boolean> =>
    new Promise<boolean>((resolve) => {
      let worker: SharedWorker;
      try {
        worker = factory();
      } catch (e) {
        logger.warn(`[Reset] Could not connect to shared worker "${workerName}":`, e);
        resolve(false);
        return;
      }
      const timeout = setTimeout(() => {
        worker.port.close();
        resolve(false);
      }, 500);
      worker.port.onmessage = (event: MessageEvent) => {
        if ((event.data as { type?: string } | null)?.type === 'terminate') {
          clearTimeout(timeout);
          worker.port.close();
          resolve(true);
        }
      };
      worker.port.start();
      worker.port.postMessage({ type: 'terminate' });
    });

  return Promise.all([
    terminateWorker(
      () => new PixelWorker({ name: 'Pixel Data Shared Worker [legacy]' }),
      'Pixel Data Shared Worker [legacy]'
    ),
    terminateWorker(
      () => new DedupedPixelWorker({ name: 'Pixel Data Shared Worker [deduped]' }),
      'Pixel Data Shared Worker [deduped]'
    ),
  ]);
}
